import { FC, useEffect, useState } from "react";
import {
  fetchTuitionEmployeeDetails,
  saveTuitionEmployeeDetails,
  searchAltNuid,
  updateManagerDetails,
} from "../../../../store/slices/Tuition/tuitionApplySlice";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import chevronRightOutlineIcon from "../../../../../public/assets/images/chevronRightOutlineIcon.svg";
import Loader from "../../../../components/common/Loader";
import Notifications from "../../../../components/common/Notifications";

const cardClass =
  "w-1/2 box-border rounded-lg! border border-gray-100 bg-[#F2F8FB]! shadow-sm !p-4";

// ============================================================================
// ICON COMPONENTS
// ============================================================================

const MinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 13H6C5.73478 13 5.48043 12.8946 5.29289 12.7071C5.10536 12.5196 5 12.2652 5 12C5 11.7348 5.10536 11.4804 5.29289 11.2929C5.48043 11.1054 5.73478 11 6 11H18C18.2652 11 18.5196 11.1054 18.7071 11.2929C18.8946 11.4804 19 11.7348 19 12C19 12.2652 18.8946 12.5196 18.7071 12.7071C18.5196 12.8946 18.2652 13 18 13Z"
      fill="#0074AD"
    />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 12C19 12.2652 18.8946 12.5196 18.7071 12.7071C18.5196 12.8946 18.2652 13 18 13H13V18C13 18.2652 12.8946 18.5196 12.7071 18.7071C12.5196 18.8946 12.2652 19 12 19C11.7348 19 11.4804 18.8946 11.2929 18.7071C11.1054 18.5196 11 18.2652 11 18V13H6C5.73478 13 5.48043 12.8946 5.29289 12.7071C5.10536 12.5196 5 12.2652 5 12C5 11.7348 5.10536 11.4804 5.29289 11.2929C5.48043 11.1054 5.73478 11 6 11H11V6C11 5.73478 11.1054 5.48043 11.2929 5.29289C11.4804 5.10536 11.7348 5 12 5C12.2652 5 12.5196 5.10536 12.7071 5.29289C12.8946 5.48043 13 5.73478 13 6V11H18C18.2652 11 18.5196 11.1054 18.7071 11.2929C18.8946 11.4804 19 11.7348 19 12Z"
      fill="#0074AD"
    />
  </svg>
);

// ============================================================================
// ERROR BANNER COMPONENT
// ============================================================================

const ErrorBanner: FC<{ error: string; title: string }> = ({ error, title }) => {
  // Determine the banner title based on error type
  let bannerTitle = "";
  if (error.includes("Invalid")) {
    bannerTitle = "Entered invalid email address";
  } else if (error.includes("NUID") || error.includes("active KP employee")) {
    bannerTitle = "The NUID you entered is not an active KP employee.";
  } else {
    bannerTitle = "E-mail addresses do not match";
  }

  return (
    <div
      className="ds-banner ds-banner__bg--white ds-banner--icon p-6! mb-4"
      data-ds-variant="basic"
      data-ds-version="1"
      data-ds-theme="vessel"
      aria-labelledby={`${title}-error-banner`}
      style={{ borderColor: "#D1480C", backgroundColor: "#FFFFFF" }}
    >
      <div className="ds-banner__icon-img">
        <img
          src="/assets/images/errorCircleSmall.svg"
          alt=""
          style={{ width: "42px", height: "42px" }}
        />
      </div>
      <div className="ds-banner__container">
        <div className="ds-banner__main flex flex-col">
          <p
            className="p1 ds-banner__main-header--awareness"
            id={`${title}-error-banner`}
            style={{
              fontWeight: 350,
              fontSize: "20px",
              marginTop: "5px",
            }}
          >
            {bannerTitle}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EMAIL INPUT FIELD COMPONENT
// ============================================================================

const EmailInputField: FC<{
  id: string;
  label: string;
  helperText: string;
  error: string;
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
}> = ({ id, label, helperText, error, value, onChange, disabled }) => (
  <>
    <label className="margin-top-2u" htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      className={`w-full min-w-0 block h-12 rounded-[4px] border border-[#878F9B] pr-10 ${
        disabled ? "ds-form__disabled-field bg-gray-100 text-gray-600 cursor-not-allowed" : "bg-white"
      } ${error ? "field-error" : ""}`}
      style={error ? { borderColor: "#dc2626", outlineColor: "#dc2626" } : {}}
      placeholder={disabled ? "" : "i.e. you@example.com"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => {
        if (error) {
          e.target.style.borderColor = "#dc2626";
          e.target.style.outlineColor = "#dc2626";
        }
      }}
      type="text"
      aria-describedby={helperText}
      disabled={disabled}
    />
    {error && (
      <p className="error-description text-[#D1480C] text-sm mt-1">
        <img
          src="/assets/images/errorCircleSmall.svg"
          alt=""
          style={{
            width: "16px",
            height: "16px",
            display: "inline",
            marginRight: "4px",
          }}
        />
        {error}
      </p>
    )}
    {!error && !disabled && (
      <p id={helperText} className="text-input-helper">
        Edit field; Must include '@' and '.com'.
      </p>
    )}
  </>
);

// ============================================================================
// EMPLOYEE DETAILS ACCORDION COMPONENT
// ============================================================================

const EmployeeDetailsAccordion: FC<{
  showEmployeeDetails: boolean;
  setShowEmployeeDetails: (show: boolean) => void;
  formData: any;
}> = ({ showEmployeeDetails, setShowEmployeeDetails, formData }) => {
  if (showEmployeeDetails) {
    return (
      <div className="margin-top-4u gap-4 flex flex-col">
        <p className="font-semibold">Job</p>
        <p>
          {formData.jobTitleCode} - {formData?.jobTitleName}
        </p>
        <p className="font-semibold">Union</p>
        <p>
          {formData?.unionId} - {formData?.unionName}
        </p>
        <p className="font-semibold">Department</p>
        <p>
          {formData?.departmentId} - {formData?.departmentDescription}
        </p>
        <p className="font-semibold">Location</p>
        <p>
          {formData?.locationId} - {formData?.locationDescription}
        </p>
        <p className="font-semibold text-sm">
          Employee information provided by HR systems. Contact your manager
          to correct any inaccurate information
        </p>
        <button
          className="flex items-center gap-2 pt-2 mt-4 text-[#0074AD]"
          data-analytics-type="button"
          data-analytics-click="TITLE"
          onClick={() => setShowEmployeeDetails(false)}
        >
          <span className="text-xl text-[#0074AD] font-semibold">
            <MinusIcon />
          </span>
          <span className="underline text-[#0074AD] font-semibold">
            Hide employee details
          </span>
        </button>
      </div>
    );
  }

  return (
    <button
      className="expandable-heading flex items-center gap-2 pt-2"
      data-analytics-type="button"
      data-analytics-click="TITLE"
      onClick={() => setShowEmployeeDetails(true)}
    >
      <span className="expandable-heading-icon text-xl text-[#0074AD] font-semibold">
        <PlusIcon />
      </span>
      <span className="expandable-heading-text underline text-[#0074AD] font-semibold">
        Show employee details
      </span>
    </button>
  );
};

// ============================================================================
// EMAIL CARD SECTION COMPONENT
// ============================================================================

const EmailCardSection: FC<{
  showEmailCard: boolean;
  setShowEmailCard: (show: boolean) => void;
  formData: any;
  newEmailError: string;
  reenteredEmailError: string;
  selectedEmail: string;
  setSelectedEmail: (email: string) => void;
  newEmail: string;
  setNewEmail: (email: string) => void;
  reenteredEmail: string;
  setReenteredEmail: (email: string) => void;
  onResetEmailRadio: () => void;
  onEmailChange: () => void;
  onResetForm: () => void;
}> = ({
  showEmailCard,
  setShowEmailCard,
  formData,
  newEmailError,
  reenteredEmailError,
  selectedEmail,
  setSelectedEmail,
  newEmail,
  setNewEmail,
  reenteredEmail,
  setReenteredEmail,
  onResetEmailRadio,
  onEmailChange,
  onResetForm,
}) => {
  if (!showEmailCard) {
    return (
      <div>
        <h3>Preferred Email address:</h3>
        <p>
          {formData?.empPrefEmail === "Work"
            ? formData?.empWorkEmail
            : formData?.empHomeEmail}
        </p>
        <button
          className="button -primary-inverted margin-top-2u"
          onClick={() => setShowEmailCard(true)}
        >
          Edit email address
        </button>
      </div>
    );
  }

  return (
    <div className={cardClass + " margin-top-2u"}>
      {(newEmailError || reenteredEmailError) && (
        <ErrorBanner error={newEmailError || reenteredEmailError} title="email" />
      )}
      <h3 className="font-semibold mb-2 ">Change email address</h3>
      <p className="text-sm mb-3">
        Select your preferred email address for education benefit
        notifications.
      </p>

      <div className="form-group">
        <fieldset className="fieldset margin-top-2u">
          {((formData?.empPrefEmail === "Work" && formData?.empWorkEmail) ||
            (formData?.empPrefEmail === "Home" && formData?.empHomeEmail)) && (
              <>
                <input
                  className="radio-button"
                  id="radio-g-0"
                  name="radio-group-0"
                  type="radio"
                  value="work"
                  checked={selectedEmail === "work"}
                  onChange={() => {
                    setSelectedEmail("work");
                    onResetEmailRadio();
                  }}
                />
                <label htmlFor="radio-g-0" className="label-ds2">
                  {formData?.empPrefEmail === "Work"
                    ? formData?.empWorkEmail
                    : formData?.empHomeEmail}
                </label>
              </>
            )}

          <input
            className="radio-button"
            id="radio-g-1"
            name="radio-group-0"
            type="radio"
            value="alternative"
            checked={selectedEmail === "alternative"}
            onChange={() => {
              setSelectedEmail("alternative");
              onResetEmailRadio();
            }}
          />
          <label htmlFor="radio-g-1" className="label-ds2">
            Alternative e-mail address
          </label>
        </fieldset>
        <div className="margin-top-2u flex flex-col">
          <EmailInputField
            id="text-input-helper-2"
            label="New preferred email address"
            helperText="helpertext"
            error={newEmailError}
            value={selectedEmail === "alternative" ? newEmail : ""}
            onChange={(val) => {
              setNewEmail(val);
            }}
            disabled={selectedEmail === "work"}
          />
          <EmailInputField
            id="text-input-helper-3"
            label="Reenter new preferred email address"
            helperText="helpertext2"
            error={reenteredEmailError}
            value={selectedEmail === "alternative" ? reenteredEmail : ""}
            onChange={(val) => {
              setReenteredEmail(val);
            }}
            disabled={selectedEmail === "work"}
          />
        </div>
        <div className="flex gap-3 margin-top-2u">
          <button className="button -secondary" onClick={onResetForm}>
            Cancel
          </button>
          <button
            className="button -primary -light"
            onClick={onEmailChange}
            disabled={selectedEmail === "work" && (!newEmail.trim() || !reenteredEmail.trim())}
          >
            Change email
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MANAGER CARD SECTION COMPONENT
// ============================================================================

const ManagerCardSection: FC<{
  showManagerCard: boolean;
  setShowManagerCard: (show: boolean) => void;
  formData: any;
  managerNuidError: string;
  managerNuid: string;
  setManagerNuid: (nuid: string) => void;
  isSearching: boolean;
  onChangeManager: () => void;
  onResetForm: () => void;
  onClearErrors: () => void;
}> = ({
  showManagerCard,
  setShowManagerCard,
  formData,
  managerNuidError,
  managerNuid,
  setManagerNuid,
  isSearching,
  onChangeManager,
  onResetForm,
  onClearErrors,
}) => {
  if (!showManagerCard) {
    return (
      <div>
        <h3>Self-Reported Manager:</h3>
        <p>{formData?.altName}</p>
        <button
          className="button -primary-inverted margin-top-2u"
          onClick={() => setShowManagerCard(true)}
        >
          Edit self-reported manager
        </button>
      </div>
    );
  }

  return (
    <div className={cardClass + " margin-top-4u"}>
      {managerNuidError && (
        <ErrorBanner error={managerNuidError} title="manager-nuid" />
      )}

      <div className="gap-3">
        <p className="p3">
          You may add or update your self-reported manager (alternate) by
          adding their NUID
        </p>
      </div>

      <div className="form-group margin-top-2u flex flex-col">
        <label
          className={`block text-sm font-medium mb-1`}
          htmlFor="manager-nuid"
        >
          Enter manager NUID
        </label>
        <input
          autoComplete="on"
          className={`w-full h-12 rounded-[4px] border bg-white min-w-0 block pr-10 text-gray-600 ${
            managerNuidError ? "field-error" : ""
          }`}
          style={
            managerNuidError
              ? { borderColor: "#dc2626", outlineColor: "#dc2626" }
              : {}
          }
          type="text"
          placeholder="Enter manager NUID"
          aria-describedby="self-reported-manager-helper"
          id="manager-nuid"
          value={managerNuid}
          onChange={(e) => {
            setManagerNuid(e.target.value);
            if (managerNuidError) onClearErrors();
          }}
          onFocus={(e) => {
            if (managerNuidError) {
              e.target.style.borderColor = "#dc2626";
              e.target.style.outlineColor = "#dc2626";
            }
          }}
        />
        {managerNuidError && (
          <p
            className="error-description text-[#D1480C] text-sm mt-1"
            id="manager-nuid-error"
          >
            <img
              src="/assets/images/errorCircleSmall.svg"
              alt=""
              style={{
                width: "16px",
                height: "16px",
                display: "inline",
                marginRight: "4px",
              }}
            />
            {managerNuidError}
          </p>
        )}
        {!managerNuidError && (
          <p className="text-input-helper mt-1">
            Edit field; Must be a valid NUID.
          </p>
        )}
      </div>

      <div className="flex gap-3 margin-top-2u">
        <button
          className="button -secondary"
          type="button"
          onClick={onResetForm}
        >
          Cancel
        </button>
        <button
          className="button -primary -light flex items-center"
          type="button"
          onClick={onChangeManager}
          disabled={isSearching}
        >
          {isSearching ? (
            "Searching..."
          ) : (
            <>
              <span>Change self-reported manager</span>
              <img
                src={chevronRightOutlineIcon}
                alt=""
                className="ml-2 inline-block w-8 h-8"
              />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// EMPLOYEE BASIC INFO COMPONENT
// ============================================================================

const EmployeeBasicInfo: FC<{ formData: any }> = ({ formData }) => (
  <div>
    <h3>Employee (NUID):</h3>
    <p>
      {formData?.empFirstName} {formData?.empMiddleName}{" "}
      {formData?.empLastName} ({formData?.empNuid})
    </p>
  </div>
);

const DirectManagerInfo: FC<{ formData: any }> = ({ formData }) => (
  <div className="margin-top-4u">
    <h3>Direct Manager:</h3>
    <p>{formData?.mgrName}</p>
  </div>
);

// ============================================================================
// EMAIL VALIDATION HELPERS
// ============================================================================

const EMAIL_VALIDATION_RULES = {
  hasAt: (email: string) => /@/.test(email),
  singleAt: (email: string) => (email.match(/@/g) || []).length === 1,
  noConsecutiveDots: (email: string) => !/\.\./.test(email),
  validChars: (email: string) => /^[a-zA-Z0-9@._-]+$/.test(email),
  noLeadingDot: (email: string) => !email.startsWith("."),
  noDotBeforeAt: (email: string) => !/\.@/.test(email),
};

const isValidEmail = (email: string): boolean => {
  if (!email) return false;

  const isValid = Object.values(EMAIL_VALIDATION_RULES).every((rule) =>
    rule(email)
  );

  if (!isValid) return false;

  const emailRegex =
    /^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// ============================================================================
// MANAGER SEARCH HANDLER
// ============================================================================

const performManagerSearch = async (
  managerNuid: string,
  dispatch: any,
  setManagerNuidError: (error: string) => void,
  resetManagerForm: () => void,
  setShowManagerSuccessBanner?: (val: boolean) => void
) => {
  const result = await dispatch(searchAltNuid(managerNuid));

  if (searchAltNuid.fulfilled.match(result)) {
      if (setShowManagerSuccessBanner) {
      setShowManagerSuccessBanner(true);
      setTimeout(() => setShowManagerSuccessBanner(false), 5000);
    }
    await dispatch(
      updateManagerDetails({ altManagerNuid: managerNuid })
    ).unwrap();
    dispatch(fetchTuitionEmployeeDetails());
    resetManagerForm();
  } else if (searchAltNuid.rejected.match(result)) {
    console.error("Manager search failed:", result.payload);
    setManagerNuidError(
      "The NUID you entered is not anactive KP employee."
    );
  }
};

// ============================================================================
// EMAIL CHANGE HANDLER
// ============================================================================

const performEmailChange = async (
  newEmailValue: string,
  dispatch: any,
  resetEmailForm: () => void
) => {
  await dispatch(
    saveTuitionEmployeeDetails({
      homeEmail: newEmailValue,
      prefEmail: "Home",
    })
  ).unwrap();
  console.log("Email updated successfully");
  resetEmailForm();
  dispatch(fetchTuitionEmployeeDetails());
};

// ============================================================================
// MAIN EMPLOYEE INFO COMPONENT
// ============================================================================

const EmployeeInfo: FC = () => {
  // State declarations
  const [showEmailCard, setShowEmailCard] = useState(false);
  const [showManagerCard, setShowManagerCard] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [managerNuid, setManagerNuid] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("work");
  const [newEmail, setNewEmail] = useState("");
  const [reenteredEmail, setReenteredEmail] = useState("");
  const [managerNuidError, setManagerNuidError] = useState("");
  const [newEmailError, setNewEmailError] = useState("");
  const [reenteredEmailError, setReenteredEmailError] = useState("");
const [showManagerSuccessBanner, setShowManagerSuccessBanner] = useState(false);
const [showSaveErrorBanner, setShowSaveErrorBanner] = useState(false);
  const dispatch = useAppDispatch();
  const { employeeDetails: formData, loading, employeeInfoPatchError } = useAppSelector(
    (state) => state.tuitionApply
  );

  useEffect(() => {
    dispatch(fetchTuitionEmployeeDetails());
  }, [dispatch]);

  // Auto-dismiss save error banner after 5 seconds
  useEffect(() => {
    if (employeeInfoPatchError) {
      setShowSaveErrorBanner(true);
      const timer = setTimeout(() => {
        setShowSaveErrorBanner(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [employeeInfoPatchError]);

  // ========== EMAIL HELPERS ==========

  const clearEmailErrors = () => {
    setNewEmailError("");
    setReenteredEmailError("");
  };

  const resetEmailForm = () => {
    setShowEmailCard(false);
    setNewEmail("");
    setReenteredEmail("");
    clearEmailErrors();
  };

  const resetEmailRadio = () => {
    setNewEmail("");
    setReenteredEmail("");
    clearEmailErrors();
  };

  const validateEmailInputs = (
    newEmailValue: string,
    reenterEmailValue: string
  ): boolean => {
    if (!isValidEmail(newEmailValue)) {
      setNewEmailError("Invalid email address. Please enter a valid email.");
      return false;
    }

    if (newEmailValue !== reenterEmailValue) {
      setReenteredEmailError("Both email addresses must match.");
      return false;
    }

    return true;
  };

  const handleEmailChange = async () => {
    const emailInput = document.getElementById(
      "text-input-helper-2"
    ) as HTMLInputElement | null;
    const reenterEmailInput = document.getElementById(
      "text-input-helper-3"
    ) as HTMLInputElement | null;

    const newEmailValue = emailInput?.value?.trim() ?? "";
    const reenterEmailValue = reenterEmailInput?.value?.trim() ?? "";

    setNewEmail(newEmailValue);
    setReenteredEmail(reenterEmailValue);

    if (!validateEmailInputs(newEmailValue, reenterEmailValue)) {
      return;
    }

    clearEmailErrors();

    try {
      console.log("Saving new email - Payload being sent:", {
        homeEmail: newEmailValue,
      });
      await performEmailChange(
        newEmailValue,
        dispatch,
        resetEmailForm
      );
    } catch (err: any) {
      console.error("Failed to update email", err);
      // Error will be displayed from Redux employeeInfoPatchError state
    }
  };

  // ========== MANAGER HELPERS ==========

  const clearManagerErrors = () => {
    setManagerNuidError("");
  };

  const resetManagerForm = () => {
    setShowManagerCard(false);
    setManagerNuid("");
    clearManagerErrors();
  };

  const handleChangeManager = async () => {
    if (!managerNuid.trim()) {
      setManagerNuidError("Manager NUID is required.");
      return;
    }

    setIsSearching(true);
    clearManagerErrors();

    try {
      await performManagerSearch(
        managerNuid,
        dispatch,
        setManagerNuidError,
        resetManagerForm,
         setShowManagerSuccessBanner
      );
    } catch (err) {
      console.error("Error searching for manager:", err);
      setManagerNuidError(
        "An error occurred while searching for the manager. Please try again."
      );
    } finally {
      setIsSearching(false);
    }
  };

  // ========== RENDER ==========

  return (
    <div>
        {showManagerSuccessBanner && (
        <Notifications messageInfo="Manager found successfully." status={true} />
      )}
      {showSaveErrorBanner && employeeInfoPatchError && (
        <Notifications messageInfo={employeeInfoPatchError} status={false} />
      )}
      <EmployeeBasicInfo formData={formData} />

      <EmailCardSection
        showEmailCard={showEmailCard}
        setShowEmailCard={setShowEmailCard}
        formData={formData}
        newEmailError={newEmailError}
        reenteredEmailError={reenteredEmailError}
        selectedEmail={selectedEmail}
        setSelectedEmail={setSelectedEmail}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        reenteredEmail={reenteredEmail}
        setReenteredEmail={setReenteredEmail}
        onResetEmailRadio={resetEmailRadio}
        onEmailChange={handleEmailChange}
        onResetForm={resetEmailForm}
      />

      <DirectManagerInfo formData={formData} />

      <ManagerCardSection
        showManagerCard={showManagerCard}
        setShowManagerCard={setShowManagerCard}
        formData={formData}
        managerNuidError={managerNuidError}
        managerNuid={managerNuid}
        setManagerNuid={setManagerNuid}
        isSearching={isSearching}
        onChangeManager={handleChangeManager}
        onResetForm={resetManagerForm}
        onClearErrors={clearManagerErrors}
      />

      <div
        className="content-toggle -variation-1 mt-8 margin-top-2u"
        data-behavior="accordion"
        data-analytics-location="Content Toggle"
      >
        <EmployeeDetailsAccordion
          showEmployeeDetails={showEmployeeDetails}
          setShowEmployeeDetails={setShowEmployeeDetails}
          formData={formData}
        />
      </div>

      {loading && <Loader />}
    </div>
  );
};

export default EmployeeInfo;